import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Info, Loader2 } from 'lucide-react';

import { getVocabularyTerm } from '@/api/vocabularies';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { useVocabularyInfo } from './useVocabularyInfo';
import { VocabTermInfoContent } from './VocabTermInfo';

/**
 * Value cap per multi-value field — an HPO category term can list ~100 associated genes; without a
 * cap the popover dwarfs its trigger. Mirrors TermInfoInline's cap.
 */
const MAX_FIELD_VALUES = 15;

interface TermInfoPopoverProps {
  /** Vocabulary the term belongs to (e.g. `hgnc`, `omim`, `hpo`). */
  readonly vocabularyId: string;
  /** Term id, prefixed form accepted (`OMIM:162200`, `HP:0001250`, `HGNC:1100`). */
  readonly termId: string;
  /** Term label — used only for the trigger's accessible name. */
  readonly termLabel: string;
  /**
   * Which side of the ⓘ the panel opens toward. Set by context so the panel opens INTO the screen,
   * not off it: the pedigree surfaces live in the right-hand rail and pass `left`; the patient
   * record has room to the right and passes `right` (the default). Radix still flips/shifts to
   * avoid a viewport collision, including the vertical clamp that keeps it off the bottom edge.
   */
  readonly side?: 'left' | 'right' | 'top' | 'bottom';
  readonly className?: string;
}

/**
 * A ⓘ trigger that opens a CLICK-disclosed floating popover with the full vocabulary-term detail —
 * the same `VocabTermInfoContent` body the admin vocabularies page and the pickers use. Built on
 * `Popover` so the content is portalled out of narrow / overflow-clipped containers (the pedigree
 * legend rail, the genetic-info rows), placed on the requested `side`, and shifted to stay on
 * screen (fixing the bottom-edge cut-off).
 *
 * The term is fetched LAZILY — the query stays disabled until the popover is first opened, so a
 * legend or a variant list with many rows does not fire a request per row on mount.
 */
export function TermInfoPopover({
  vocabularyId,
  termId,
  termLabel,
  side = 'right',
  className,
}: TermInfoPopoverProps): ReactElement {
  const { t } = useTranslation();
  const [everOpened, setEverOpened] = useState(false);
  const info = useVocabularyInfo(vocabularyId);
  const termQuery = useQuery({
    queryKey: ['vocabulary', vocabularyId, 'term', termId],
    queryFn: ({ signal }) => getVocabularyTerm(vocabularyId, termId, signal),
    enabled: everOpened,
    staleTime: 5 * 60_000,
  });

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) setEverOpened(true);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('vocab.picker.info', { term: termLabel })}
          // Not a drag source: the legend rows this sits inside are draggable onto nodes, so keep
          // pointer interactions here from starting a row drag.
          draggable={false}
          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerDown={(e) => { e.stopPropagation(); }}
          className={cn(
            'shrink-0 inline-flex items-center rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors',
            className,
          )}
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align="start" className="w-80 max-w-[calc(100vw-2rem)] p-3">
        {termQuery.isError ? (
          <p className="text-xs text-destructive">{t('vocab.picker.error')}</p>
        ) : termQuery.data === null ? (
          <p className="text-xs text-muted-foreground">{t('vocab.picker.empty')}</p>
        ) : termQuery.data === undefined ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {t('patient.phenotypes.browse.loading')}
          </p>
        ) : (
          <VocabTermInfoContent
            term={termQuery.data}
            display={info?.display ?? null}
            vocabularyName={info?.name ?? null}
            version={info?.dataVersion ?? null}
            maxValuesPerField={MAX_FIELD_VALUES}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
